import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { getContrastColor } from '@/lib/colorUtils';
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, RefreshCw, Share2, ArrowLeft, LayoutDashboard, MessageSquare, Building2, ClipboardCheck, Sparkles, Palette, MapPin, Lightbulb, Menu, Monitor, Smartphone, BarChart, ListChecks, Paperclip } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { NorthDesignSettings } from "@/components/NorthDesignSettings";
import { NorthAISummaries } from "@/components/NorthAISummaries";
import { NorthDocuments } from "@/components/NorthDocuments";
import { SocialMediaManager } from "@/components/SocialMediaManager";
import { ListingPerformance } from "@/components/ListingPerformance";
import SocialCampaigns from "@/components/SocialCampaigns";
import { ShowingsPreview } from "@/components/ShowingsPreview";
import { CompsManager } from "@/components/CompsManager";
import { CompsPreview } from "@/components/CompsPreview";
import { ShareDialog } from "@/components/ShareDialog";
import { useTeamUsage } from "@/hooks/useTeamUsage";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { OverLimitDialog } from "@/components/OverLimitDialog";
import { NeighborhoodData } from "@/components/NeighborhoodData";
import { NeighborhoodDataPreview } from "@/components/NeighborhoodDataPreview";
import { FloorplanTourPreview } from "@/components/FloorplanTourPreview";
import { FloorplanTourManager } from "@/components/FloorplanTourManager";
import { AgentInsightsSection } from "@/components/AgentInsightsSection";
import { logger } from "@/lib/logger";
import { AgentInsightsManager } from "@/components/AgentInsightsManager";
import { NorthLoadingProgress } from "@/components/NorthLoadingProgress";
import { useDefaultLogo } from "@/hooks/useDefaultLogo";
import { formatMarkdownToSafeHtml } from '@/lib/sanitize';
import ShowingsManager from "@/components/ShowingsManager";
import { ViewsSavesManager } from "@/components/ViewsSavesManager";
import { SocialCampaignsPreviewWrapper } from "@/components/SocialCampaignsPreviewWrapper";
import { ShowingsPreviewWrapper } from "@/components/ShowingsPreviewWrapper";
import TasksManager from "@/components/TasksManager";
import { TasksPreviewWrapper } from "@/components/TasksPreviewWrapper";
import { useTour, TourStep } from "@/components/tour/TourProvider";
import { fetchTourFlags, markTourSeen } from "@/lib/tourProgress";

interface North {
  id: string;
  zillow_url: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  mls_id: string | null;
  property_type: string | null;
  beds: number | null; // Database column name
  baths: number | null; // Database column name
  sqft: number | null; // Database column name
  price: number | null;
  status: string;
  image_url: string | null;
  created_by: string | null;
  listing_agent: string;
  listing_agent_photo: string | null;
  views_count: number | null;
  saves_count: number | null;
  realtor_views: number | null;
  realtor_saves: number | null;
  total_zillow_views: number | null;
  total_zillow_saves: number | null;
  total_realtor_views: number | null;
  total_realtor_saves: number | null;
  total_homes_views: number | null;
  total_homes_saves: number | null;
  total_redfin_views: number | null;
  total_redfin_saves: number | null;
  total_trulia_views: number | null;
  total_trulia_saves: number | null;
  zillow_views_as_of: string | null;
  realtor_views_as_of: string | null;
  homes_views_as_of: string | null;
  redfin_views_as_of: string | null;
  trulia_views_as_of: string | null;
  showings_count: number | null;
  price_changes_count: number | null;
  price_history: any[] | null;
  description: string | null;
  date_created: string;
  last_refreshed: string;
  year_built: number | null;
  custom_agent_name: string | null;
  custom_agent_headshot_url: string | null;
  custom_logo_url: string | null;
  primary_brand_color: string | null;
  secondary_brand_color: string | null;
  days_on_zillow: number | null;
  executive_summary: string | null;
  executive_summary_updated_at: string | null;
  showings_summary: string | null;
  showings_summary_updated_at: string | null;
  comps_summary: string | null;
  comps_summary_updated_at: string | null;
  neighborhood_summary: string | null;
  neighborhood_summary_updated_at: string | null;
  neighborhood_data_updated_at: string | null;
  is_published: boolean;
  published_at: string | null;
  parent_north_id: string | null;
  is_draft: boolean;
  show_neighborhood_on_report: boolean;
  neighborhood_data: any;
  zpid: string | null;
  floorplan_data: any;
  floorplan_updated_at: string | null;
  tour_data: any;
  tour_updated_at: string | null;
  show_social_on_report: boolean;
  show_showings_on_report: boolean;
  show_floorplan_tour_on_report: boolean;
  show_comps_on_report: boolean;
  show_tasks_on_report: boolean;
  realtor_url: string | null;
}

interface Profile {
  north_logo_url: string | null;
  company_logo_url: string | null;
  full_name: string | null;
  avatar_url: string | null;
  default_primary_color: string | null;
  default_secondary_color: string | null;
}

function SellerReport() {
  // ALL HOOKS MUST BE CALLED IN THE SAME ORDER EVERY RENDER
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { getEffectiveUserId, isImpersonating, impersonatedUser } = useImpersonation();
  const { checkLimit, showUpgradePrompt, upgradeDialogOpen, setUpgradeDialogOpen, upgradeFeature, subscription, refreshUsage, planLimits, usage, loading: usageLoading } = useTeamUsage();
  const { defaultLogoUrl } = useDefaultLogo();
  const { startTour, isActive: tourActive } = useTour();
  const [hasSeenReportTour, setHasSeenReportTour] = useState<boolean | null>(null);
  const [isStartingReportTour, setIsStartingReportTour] = useState(false);
  
  // State hooks - must be in consistent order
  const [north, setNorth] = useState<North | null>(null);
  const [parentNorth, setParentNorth] = useState<North | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [teamName, setTeamName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshingNorthId, setRefreshingNorthId] = useState<string | null>(null);
  const [initialTimestamps, setInitialTimestamps] = useState<{
    executive_summary_updated_at?: string | null;
    comps_summary_updated_at?: string | null;
    neighborhood_summary_updated_at?: string | null;
    showings_summary_updated_at?: string | null;
  } | null>(null);
  const refreshCompleteTimeRef = useRef<number | null>(null); // Track when refresh completed to add cooldown
  const fetchInProgressRef = useRef<boolean>(false); // Track if fetchNorthData is currently running
  const lastFetchTimestampRef = useRef<number>(0); // Track timestamp of last fetch to prevent stale updates
  const isRefreshingRef = useRef<boolean>(false); // Track isRefreshing state in ref to avoid recreating subscription
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Initialize activeSection from URL, default to 'preview'
  // Don't use searchParams in initializer - it's a hook result and can cause hook order issues
  const [activeSection, setActiveSection] = useState('preview');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'mobile' | 'desktop'>('desktop');
  const [showOverLimitDialog, setShowOverLimitDialog] = useState(false);
  const [hasCheckedOverLimit, setHasCheckedOverLimit] = useState(false);
  useEffect(() => {
    const loadFlags = async () => {
      try {
        const flags = await fetchTourFlags();
        setHasSeenReportTour(flags.reportSeen);
      } catch (error) {
        logger.error("Failed to load report tour flags", error);
        setHasSeenReportTour(true);
      }
    };
    loadFlags();
  }, []);
  
  // Early return AFTER all hooks are called (React requirement)
  if (!id) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">Invalid report ID</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Helper functions - moved after early return to avoid initialization issues
  const lightenColorWithWhite = useCallback((hex: string, whitePercentage: number = 50): string => {
    // Remove # if present
    const cleanHex = hex.replace('#', '');

    // Parse RGB values
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    // Calculate how much white to add (0-255)
    const whiteAmount = (255 * whitePercentage) / 100;

    // Add white to each channel and clamp to 255
    const newR = Math.min(255, Math.round(r + whiteAmount));
    const newG = Math.min(255, Math.round(g + whiteAmount));
    const newB = Math.min(255, Math.round(b + whiteAmount));

    // Convert back to hex
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }, []);

  // Convert hex color to HSL format that Tailwind expects (without the hsl() wrapper)
  const hexToHsl = useCallback((hex: string): string => {
    // Remove # if present
    const cleanHex = hex.replace('#', '');

    // Parse RGB values
    const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
    const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
    const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    // Convert to degrees and percentages, return in format Tailwind expects
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  }, []);

  // Compute effective brand colors based on use_custom_branding toggle
  // Default to false (team branding) if null/undefined
  // Moved after all hooks to ensure proper initialization order
  const effectivePrimaryColor = useMemo(() => {
    if (!north || !profile) return '#0c0f24';
    const useCustom = (north as any).use_custom_branding === true;
    return useCustom
      ? (north.primary_brand_color || profile.default_primary_color || '#0c0f24')
      : (profile.default_primary_color || '#0c0f24');
  }, [north, profile]);

  const effectiveSecondaryColor = useMemo(() => {
    if (!north || !profile) return '#1b489b';
    const useCustom = (north as any).use_custom_branding === true;
    return useCustom
      ? (north.secondary_brand_color || profile.default_secondary_color || '#1b489b')
      : (profile.default_secondary_color || '#1b489b');
  }, [north, profile]);

  const effectiveLogoUrl = useMemo(() => {
    if (!north || !profile) return null;
    const useCustom = (north as any).use_custom_branding === true;
    return useCustom
      ? (north.custom_logo_url || profile.company_logo_url)
      : profile.company_logo_url;
  }, [north, profile]);

  // Helper to convert hex to RGB for gradient backgrounds
  const hexToRgb = useCallback((hex: string): { r: number; g: number; b: number } => {
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return { r, g, b };
  }, []);

  // Generate gradient style for Market Pulse section using branding colors
  const getMarketPulseGradient = useMemo(() => {
    if (!effectiveSecondaryColor) {
      return { background: 'linear-gradient(to bottom right, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03), transparent)' };
    }
    const rgb = hexToRgb(effectiveSecondaryColor);
    return {
      background: `linear-gradient(to bottom right, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05), rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.03), transparent)`
    };
  }, [effectiveSecondaryColor, hexToRgb]);

  // Recalculate hover color whenever effective secondary color changes
  const hoverColor = useMemo(() => {
    const calculated = lightenColorWithWhite(effectiveSecondaryColor, 50);
    logger.log('SellerReport - Calculated hover color from:', effectiveSecondaryColor, 'to:', calculated);
    return calculated;
  }, [effectiveSecondaryColor, lightenColorWithWhite]);

  // Helper function to update active section and sync with URL
  const updateActiveSection = useCallback((section: string) => {
    setActiveSection(section);
    const newSearchParams = new URLSearchParams(searchParams);
    if (section === 'preview') {
      // Remove section param for preview (default)
      newSearchParams.delete('section');
    } else {
      newSearchParams.set('section', section);
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync URL changes to state (for browser back/forward) - initialize on mount
  useEffect(() => {
    const sectionFromUrl = searchParams.get('section');
    const validSections = ['preview', 'social', 'showings', 'tasks', 'views-saves', 'floorplan-tour', 'comps', 'neighborhood', 'agent-insights', 'ai', 'documents', 'design'];
    const newSection = sectionFromUrl && validSections.includes(sectionFromUrl) ? sectionFromUrl : 'preview';
    // Initialize or update if different to avoid unnecessary re-renders
    setActiveSection(prev => {
      // If prev is still 'preview' (initial state), always update to match URL
      if (prev === 'preview' && newSection !== 'preview') {
        return newSection;
      }
      // Otherwise only update if different
      return prev !== newSection ? newSection : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams, not activeSection to avoid loops

  const fetchNorthData = useCallback(async () => {
    // Prevent duplicate simultaneous fetches
    if (fetchInProgressRef.current) {
      return;
    }

    // id is guaranteed to exist due to early return in component

    const fetchStartTime = Date.now();
    fetchInProgressRef.current = true;

    try {
      const { data, error } = await supabase
        .from('norths')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) {
        logger.error('❌ SellerReport: Error fetching north data:', error);
        // Check if it's an RLS/permission error
        if (error.code === 'PGRST301' || error.message?.includes('permission') || error.message?.includes('row-level security')) {
          toast({
            title: "Permission Denied",
            description: "You don't have permission to view this North report. Please refresh the page.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: `Failed to load North: ${error.message || 'Unknown error'}`,
            variant: "destructive",
          });
        }
        setIsLoading(false);
        fetchInProgressRef.current = false;
        return;
      }
      
      if (!data) {
        logger.error('❌ SellerReport: North not found for ID:', id);
        toast({
          title: "Error",
          description: "North not found",
          variant: "destructive",
        });
        setIsLoading(false);
        fetchInProgressRef.current = false;
        return;
      }

      // Only update state if this fetch is newer than the last one
      // This prevents stale data from overwriting fresh data
      if (fetchStartTime < lastFetchTimestampRef.current) {
        return;
      }

      lastFetchTimestampRef.current = fetchStartTime;

      const northData = {
        ...data,
        price_history: Array.isArray(data.price_history) ? data.price_history : [],
      } as unknown as North;

      // Only update if the fetched data is actually newer (based on summary timestamps)
      setNorth(prevNorth => {
        if (!prevNorth) {
          return northData;
        }

        // Compare summary timestamps to ensure we're not reverting to older data
        const prevExecTimestamp = prevNorth.executive_summary_updated_at ? new Date(prevNorth.executive_summary_updated_at).getTime() : 0;
        const newExecTimestamp = northData.executive_summary_updated_at ? new Date(northData.executive_summary_updated_at).getTime() : 0;
        
        const prevCompsTimestamp = prevNorth.comps_summary_updated_at ? new Date(prevNorth.comps_summary_updated_at).getTime() : 0;
        const newCompsTimestamp = northData.comps_summary_updated_at ? new Date(northData.comps_summary_updated_at).getTime() : 0;
        
        const prevNeighborhoodTimestamp = prevNorth.neighborhood_summary_updated_at ? new Date(prevNorth.neighborhood_summary_updated_at).getTime() : 0;
        const newNeighborhoodTimestamp = northData.neighborhood_summary_updated_at ? new Date(northData.neighborhood_summary_updated_at).getTime() : 0;

        // If any summary timestamp in the new data is older than the previous, log a warning
        if (newExecTimestamp > 0 && prevExecTimestamp > 0 && newExecTimestamp < prevExecTimestamp) {
          logger.warn('⚠️ SellerReport: New executive summary timestamp is older than previous, keeping previous data');
          return prevNorth;
        }
        if (newCompsTimestamp > 0 && prevCompsTimestamp > 0 && newCompsTimestamp < prevCompsTimestamp) {
          logger.warn('⚠️ SellerReport: New comps summary timestamp is older than previous, keeping previous data');
          return prevNorth;
        }
        if (newNeighborhoodTimestamp > 0 && prevNeighborhoodTimestamp > 0 && newNeighborhoodTimestamp < prevNeighborhoodTimestamp) {
          logger.warn('⚠️ SellerReport: New neighborhood summary timestamp is older than previous, keeping previous data');
          return prevNorth;
        }

        return northData;
      });

      // If there's a parent_north_id, fetch the parent's visibility settings and neighborhood data
      if (northData.parent_north_id) {
        const { data: parentData, error: parentError } = await supabase
          .from('norths')
          .select('show_comps_on_report, show_social_on_report, show_showings_on_report, show_floorplan_tour_on_report, show_neighborhood_on_report, neighborhood_data, neighborhood_data_updated_at, neighborhood_summary')
          .eq('id', northData.parent_north_id)
          .maybeSingle();

        if (!parentError && parentData) {
          setParentNorth(parentData as unknown as North);
          
          // Merge parent's neighborhood data into current north state for preview
          setNorth(prev => prev ? {
            ...prev,
            neighborhood_data: parentData.neighborhood_data || prev.neighborhood_data,
            neighborhood_data_updated_at: parentData.neighborhood_data_updated_at || prev.neighborhood_data_updated_at,
            neighborhood_summary: parentData.neighborhood_summary || prev.neighborhood_summary,
            show_neighborhood_on_report: parentData.show_neighborhood_on_report !== undefined ? parentData.show_neighborhood_on_report : prev.show_neighborhood_on_report
          } : prev);
        }
      } else {
        setParentNorth(null);
      }
    } catch (error) {
      logger.error('Error fetching North:', error);
      toast({
        title: "Error",
        description: "Failed to load North data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [id, queryClient, toast, getEffectiveUserId, navigate]);

  const fetchProfile = useCallback(async () => {
    try {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('north_logo_url, company_logo_url, full_name, avatar_url, default_primary_color, default_secondary_color, team_id')
        .eq('user_id', effectiveUserId)
        .maybeSingle();

      if (error) throw error;

      // Fetch team name - do this first before any early returns
      if (data?.team_id) {
        const { data: team } = await supabase
          .from('teams')
          .select('name')
          .eq('id', data.team_id)
          .single();
        if (team?.name) {
          setTeamName(team.name);
        }
      }

      // For non-admin users, load team admin's branding
      if (data?.team_id) {
        // Get admin user IDs
        const { data: adminRoles } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'admin');

        if (adminRoles && adminRoles.length > 0) {
          const adminUserIds = adminRoles.map(r => r.user_id);

          // Get admin profile for this team
          const { data: adminProfile } = await supabase
            .from('profiles')
            .select('north_logo_url, company_logo_url, full_name, avatar_url, default_primary_color, default_secondary_color')
            .eq('team_id', data.team_id)
            .in('user_id', adminUserIds)
            .maybeSingle();

          // Use admin's profile for branding, but keep current user's name/avatar
          if (adminProfile) {
            setProfile({
              ...adminProfile,
              full_name: data.full_name, // Keep user's own name
              avatar_url: data.avatar_url // Keep user's own avatar
            });
            logger.log('Loaded team admin brand colors for agent:', {
              primary: adminProfile.default_primary_color,
              secondary: adminProfile.default_secondary_color
            });
            // Don't return early - team name is already set above
            return;
          }
        }
      }

      // Fallback to user's own profile
      setProfile(data);
      logger.log('Loaded profile brand colors:', {
        primary: data?.default_primary_color,
        secondary: data?.default_secondary_color
      });
    } catch (error) {
      logger.error('Error fetching profile:', error);
    }
  }, [getEffectiveUserId]);

  // Set up data fetching and real-time subscription - MUST be after fetchNorthData and fetchProfile are defined
  useEffect(() => {
    // id is guaranteed to exist due to early return above
    // Call async functions - they handle their own errors
    fetchNorthData().catch(err => {
      logger.error('Error in fetchNorthData:', err);
    });
    fetchProfile().catch(err => {
      logger.error('Error in fetchProfile:', err);
    });

    // Set up real-time subscription for changes to this north
    let debounceTimer: NodeJS.Timeout | null = null;
    
    const channel = supabase
      .channel(`north-seller-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'norths',
          filter: `id=eq.${id}`,
        },
        (payload) => {
            // Only refetch if relevant fields actually changed (avoid cascading updates)
            const oldData = payload.old as any;
            const newData = payload.new as any;
            
            // Check if actual metric values changed (not just timestamps)
            const metricsChanged = 
              (oldData?.realtor_views !== newData?.realtor_views) ||
              (oldData?.realtor_saves !== newData?.realtor_saves) ||
              (oldData?.views_count !== newData?.views_count) ||
              (oldData?.saves_count !== newData?.saves_count);
            
            // Check if summary fields changed (for auto-updating UI when summaries are generated)
            const summariesChanged = 
              (oldData?.executive_summary !== newData?.executive_summary) ||
              (oldData?.executive_summary_updated_at !== newData?.executive_summary_updated_at) ||
              (oldData?.showings_summary !== newData?.showings_summary) ||
              (oldData?.showings_summary_updated_at !== newData?.showings_summary_updated_at) ||
              (oldData?.comps_summary !== newData?.comps_summary) ||
              (oldData?.comps_summary_updated_at !== newData?.comps_summary_updated_at) ||
              (oldData?.neighborhood_summary !== newData?.neighborhood_summary) ||
              (oldData?.neighborhood_summary_updated_at !== newData?.neighborhood_summary_updated_at);
            
            // Only check URL change if it's actually different (and not null/undefined)
            const urlChanged = oldData?.realtor_url !== newData?.realtor_url && 
                              newData?.realtor_url && 
                              oldData?.realtor_url;
            
            // Check if neighborhood_data was updated
            const neighborhoodDataChanged = 
              oldData?.neighborhood_data_updated_at !== newData?.neighborhood_data_updated_at ||
              (oldData?.neighborhood_data === null && newData?.neighborhood_data !== null);
            
            // Don't trigger real-time updates if we're currently refreshing (to prevent duplicate generation)
            // The refresh process will handle UI updates when it completes
            // Use ref instead of state to avoid dependency issues
            if (isRefreshingRef.current) {
              return;
            }
            
            // Add cooldown period after refresh completes (8 seconds) to prevent immediate duplicate updates
            // Extended to 8 seconds to ensure all database writes, cache propagation, and state updates complete
            if (refreshCompleteTimeRef.current) {
              const timeSinceRefresh = Date.now() - refreshCompleteTimeRef.current;
              if (timeSinceRefresh < 8000) {
                return;
              }
              // Clear cooldown after it expires
              if (timeSinceRefresh >= 8000) {
                refreshCompleteTimeRef.current = null;
              }
            }
            
            // Check if this is actually a meaningful change (not just the same values)
            // Sometimes Supabase sends updates even when values haven't actually changed
            // For summaries, we need to check if the timestamp is actually NEWER (not just different)
            const actuallyChanged = 
              (metricsChanged && (
                oldData?.realtor_views !== newData?.realtor_views ||
                oldData?.realtor_saves !== newData?.realtor_saves ||
                oldData?.views_count !== newData?.views_count ||
                oldData?.saves_count !== newData?.saves_count
              )) ||
              (urlChanged && oldData?.realtor_url !== newData?.realtor_url) ||
              (neighborhoodDataChanged && (
                oldData?.neighborhood_data_updated_at !== newData?.neighborhood_data_updated_at ||
                (oldData?.neighborhood_data === null && newData?.neighborhood_data !== null)
              )) ||
              (summariesChanged && (
                // For summary timestamps, only trigger if the new timestamp is actually newer (not older)
                (oldData?.executive_summary_updated_at && newData?.executive_summary_updated_at && 
                 new Date(newData.executive_summary_updated_at) > new Date(oldData.executive_summary_updated_at)) ||
                (oldData?.showings_summary_updated_at && newData?.showings_summary_updated_at && 
                 new Date(newData.showings_summary_updated_at) > new Date(oldData.showings_summary_updated_at)) ||
                (oldData?.comps_summary_updated_at && newData?.comps_summary_updated_at && 
                 new Date(newData.comps_summary_updated_at) > new Date(oldData.comps_summary_updated_at)) ||
                (oldData?.neighborhood_summary_updated_at && newData?.neighborhood_summary_updated_at && 
                 new Date(newData.neighborhood_summary_updated_at) > new Date(oldData.neighborhood_summary_updated_at)) ||
                // Allow if old was null and new has a value (new summary created)
                (!oldData?.executive_summary_updated_at && newData?.executive_summary_updated_at) ||
                (!oldData?.showings_summary_updated_at && newData?.showings_summary_updated_at) ||
                (!oldData?.comps_summary_updated_at && newData?.comps_summary_updated_at) ||
                (!oldData?.neighborhood_summary_updated_at && newData?.neighborhood_summary_updated_at)
              ));
            
            // Only refetch if there's an actual meaningful change
            if (actuallyChanged) {
              // Debounce to prevent cascading updates
              if (debounceTimer) {
                clearTimeout(debounceTimer);
              }
              debounceTimer = setTimeout(() => {
                // Double-check we're not refreshing before actually fetching
                // Use ref instead of state to avoid dependency issues
                if (!isRefreshingRef.current && !refreshCompleteTimeRef.current && !fetchInProgressRef.current) {
                  // Use the stable fetchNorthData from useCallback
                  fetchNorthData();
                  // Also invalidate React Query caches for summaries if they changed
                  if (summariesChanged && id) {
                    queryClient.invalidateQueries({ queryKey: ['ai-summaries', id] });
                    queryClient.invalidateQueries({ queryKey: ['north-showings-summary', id] });
                    queryClient.invalidateQueries({ queryKey: ['agent-insights-summary', id] });
                  }
                }
              }, 1500); // Increased to 1.5 seconds to better batch multiple rapid updates and avoid race conditions
            }
          }
        )
        .subscribe();

    // Cleanup subscription on unmount
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      supabase.removeChannel(channel);
    };
  }, [id, fetchNorthData, fetchProfile, queryClient, getEffectiveUserId]); // Include all dependencies

  // Check if user is over their plan limit
  useEffect(() => {
    if (!usageLoading && usage && planLimits && !hasCheckedOverLimit) {
      setHasCheckedOverLimit(true);
      if (planLimits.max_active_norths !== null && usage.norths_created > planLimits.max_active_norths) {
        setShowOverLimitDialog(true);
      }
    }
  }, [usageLoading, usage, planLimits, hasCheckedOverLimit]);

  const handleRefreshNorth = async () => {
    if (!north?.zillow_url || !id) return;

    // Prevent double-clicks/race conditions
    if (isRefreshing) {
      return;
    }

    // CRITICAL: Capture initial timestamps BEFORE setting isRefreshing
    // This ensures we capture the state before any operations begin
    const initialTimestamps = {
      executive_summary_updated_at: north.executive_summary_updated_at,
      comps_summary_updated_at: north.comps_summary_updated_at,
      neighborhood_summary_updated_at: north.neighborhood_summary_updated_at,
      showings_summary_updated_at: north.showings_summary_updated_at,
    };
    
    setIsRefreshing(true);
    isRefreshingRef.current = true; // Update ref immediately
    setInitialTimestamps(initialTimestamps);

      // Show progress dialog immediately
      setRefreshingNorthId(id);
      
      // Track when refresh started to ensure minimum display time
      (window as any).refreshStartTime = Date.now();

      try {
      // Get current user for job creation
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to refresh a North",
          variant: "destructive",
        });
        setRefreshingNorthId(null);
        setIsRefreshing(false);
        isRefreshingRef.current = false; // Update ref immediately
        return;
      }

      // Get user's profile to fetch team_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .maybeSingle();

      const teamId = profile?.team_id || null;

      // Re-scrape Zillow data (this happens while dialog shows "Scraping property data...")
      // Prepare headers for impersonation if active
      const headers: Record<string, string> = {};
      if (isImpersonating && impersonatedUser?.id) {
        headers['X-Impersonated-User-Id'] = impersonatedUser.id;
      }
      
      const { data: scrapedData, error: scrapeError } = await supabase.functions.invoke(
        "scrape-zillow",
        {
          body: { zillowUrl: north.zillow_url },
          headers,
        }
      );

      if (scrapeError) {
        logger.error('❌ REFRESH: Zillow scrape error:', scrapeError);
        
        // Extract error message from the error object
        let errorMessage = 'Failed to refresh Zillow data.';
        if (scrapeError.message) {
          errorMessage = scrapeError.message;
        } else if (typeof scrapeError === 'object' && 'error' in scrapeError) {
          errorMessage = String(scrapeError.error);
        }
        
        // Check if it's a validation error
        if (typeof scrapeError === 'object' && 'details' in scrapeError && Array.isArray(scrapeError.details)) {
          const validationDetails = scrapeError.details.map((d: any) => d.message || d).join(', ');
          errorMessage = `Validation error: ${validationDetails}`;
        }
        
        toast({
          title: "Refresh Error",
          description: errorMessage,
          variant: "destructive",
        });
        
        setRefreshingNorthId(null);
        setIsRefreshing(false);
        isRefreshingRef.current = false; // Update ref immediately
        return;
      }
      
      // Parse daysOnZillow safely (in case it comes as "38 days" string)
      let parsedDaysOnZillow = scrapedData.daysOnZillow;
      if (typeof parsedDaysOnZillow === 'string') {
        const match = parsedDaysOnZillow.match(/(\d+)/);
        parsedDaysOnZillow = match ? parseInt(match[1], 10) : null;
      }

      const zillowViews = scrapedData.zillowViews ?? 0;
      const zillowSaves = scrapedData.zillowSaves ?? 0;

      // Fetch Realtor.com metrics
      let realtorViews = north.realtor_views || 0;
      let realtorSaves = north.realtor_saves || 0;
      let finalRealtorUrl = north.realtor_url || null;
      let successfulMlsId: string | null = null;
      
      // Use stored Realtor.com URL if available, otherwise try MLS ID lookup
      if (finalRealtorUrl && finalRealtorUrl.trim()) {
        try {
          const { data: realtorData, error: realtorError } = await supabase.functions.invoke(
            "fetch-realtor-metrics",
            {
              body: { realtorUrl: finalRealtorUrl.trim() },
              headers,
            }
          );

          if (!realtorError && realtorData) {
            realtorViews = realtorData.views || 0;
            realtorSaves = realtorData.saves || 0;
            // Update URL if we got a new one back
            if (realtorData.realtorUrl) {
              finalRealtorUrl = realtorData.realtorUrl;
            }
          }
        } catch (realtorErr) {
          logger.error('Realtor.com metrics refresh error:', realtorErr);
        }
      }

      // Update the north with new scraped data
      // On manual refresh, always use fresh metrics (default to 0 if null/undefined)
      // This ensures views/saves are always updated, even if API returns null
      const { error: updateError } = await supabase
        .from("norths")
        .update({
          zpid: scrapedData.zpid,
          address: scrapedData.address || north.address,
          city: scrapedData.city || north.city,
          state: scrapedData.state || north.state,
          zip: scrapedData.zip || north.zip,
          mls_id: successfulMlsId || scrapedData.mlsId || north.mls_id,
          property_type: scrapedData.propertyType,
          bedrooms: scrapedData.bedrooms,
          bathrooms: scrapedData.bathrooms,
          square_feet: scrapedData.squareFeet,
          lot_size: scrapedData.lotSize,
          year_built: scrapedData.yearBuilt,
          price: scrapedData.price,
          image_url: scrapedData.imageUrl,
          additional_images: scrapedData.additionalImages,
          description: scrapedData.description,
          features: scrapedData.features,
          listing_agent: scrapedData.listingAgent || north.listing_agent,
          listing_agent_photo: scrapedData.listingAgentPhoto,
          // Always use fresh metrics on refresh (default to 0 if null/undefined)
          views_count: zillowViews,
          saves_count: zillowSaves,
          realtor_views: realtorViews,
          realtor_saves: realtorSaves,
          realtor_url: finalRealtorUrl, // Update Realtor.com URL
          days_on_zillow: parsedDaysOnZillow ?? north.days_on_zillow,
          price_history: scrapedData.priceHistory || north.price_history,
          last_refreshed: new Date().toISOString(),
        })
        .eq("id", id);

      if (updateError) {
        logger.error('Failed to update North metrics:', updateError);
        throw updateError;
      }

      // Verify the update by fetching the updated record
      const { data: updatedNorth, error: verifyError } = await supabase
        .from('norths')
        .select('views_count, saves_count, realtor_views, realtor_saves')
        .eq('id', id)
        .single();

      if (verifyError) {
        logger.error('Failed to verify metrics update:', verifyError);
      }

      // Immediately refresh the UI with updated metrics (before jobs complete)
      await fetchNorthData();
      queryClient.invalidateQueries({ queryKey: ['comps-preview', id] });
      queryClient.invalidateQueries({ queryKey: ['floorplan-tour', id] });
      queryClient.invalidateQueries({ queryKey: ['neighborhood-data', id] });
      queryClient.invalidateQueries({ queryKey: ['ai-summaries', id] });
      queryClient.invalidateQueries({ queryKey: ['north-showings-summary', id] });
      queryClient.invalidateQueries({ queryKey: ['agent-insights-summary', id] });

      // CACHE-AWARE JOB CREATION
      // Only create jobs for data that's stale (> 24 hours old)
      // AI summaries are always regenerated to stay fresh
      logger.log('Creating cache-aware refresh jobs for north:', id);

      // Fetch current north data to check cache timestamps
      const { data: currentNorth } = await supabase
        .from('norths')
        .select('*')
        .eq('id', id)
        .single();

      // Helper function to check if data is stale (> 24 hours)
      const isCacheStale = (timestamp: string | null): boolean => {
        if (!timestamp) return true; // No cache, needs fetch
        const cacheDate = new Date(timestamp);
        const now = new Date();
        const ageHours = (now.getTime() - cacheDate.getTime()) / (1000 * 60 * 60);
        return ageHours >= 24; // Stale if 24+ hours old
      };

      // Check which data needs refreshing
      const needsComps = isCacheStale(
        // Check if comps exist by looking at comparable_properties table
        (await supabase
          .from('comparable_properties')
          .select('created_at')
          .eq('north_id', id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        ).data?.created_at || null
      );

      const needsFloorplanTour =
        isCacheStale(currentNorth?.floorplan_updated_at) ||
        isCacheStale(currentNorth?.tour_updated_at);

      const needsNeighborhoodData = isCacheStale(currentNorth?.neighborhood_data_updated_at);

      // Count how many API calls we're saving
      const cachedDataTypes = [];
      if (!needsComps) cachedDataTypes.push('Comps');
      if (!needsFloorplanTour) cachedDataTypes.push('Floorplan/Tour');
      if (!needsNeighborhoodData) cachedDataTypes.push('Neighborhood');

      logger.log('Cache status:', {
        needsComps,
        needsFloorplanTour,
        needsNeighborhoodData,
        compsAge: currentNorth?.floorplan_updated_at,
        floorplanAge: currentNorth?.floorplan_updated_at,
        tourAge: currentNorth?.tour_updated_at,
        neighborhoodAge: currentNorth?.neighborhood_data_updated_at,
        cachedData: cachedDataTypes.length > 0 ? cachedDataTypes.join(', ') : 'None',
        apiCallsSaved: cachedDataTypes.length
      });

      // Mark north as processing so the progress dialog waits for all jobs to finish
      // (The job processor sets is_processing back to false when all jobs complete)
      await supabase
        .from('norths')
        .update({ is_processing: true })
        .eq('id', id);

      // CRITICAL: Delete existing jobs FIRST to prevent duplicates
      // This must happen before building the jobs array to ensure a clean slate
      const { error: deleteError } = await supabase
        .from('north_processing_jobs' as any)
        .delete()
        .eq('north_id', id);
      
      if (deleteError) {
        logger.error('Error deleting existing jobs:', deleteError);
      }

      const jobs = [];

      // Only create fetch jobs for stale data
      if (needsComps) {
        logger.log('📥 Comps cache is stale - will fetch fresh data');
        jobs.push({
          north_id: id,
          user_id: user.id,
          team_id: teamId,
          job_type: 'fetch_comps',
          priority: 10,
          config: { zillowUrl: north.zillow_url }
        });
      } else {
        logger.log('✓ Comps cache is fresh - skipping fetch');
      }

      // Always regenerate comps summary (AI summaries should always be fresh)
      jobs.push({
        north_id: id,
        user_id: user.id,
        team_id: teamId,
        job_type: 'generate_comps_summary',
        priority: 9,
        config: {}
      });

      if (needsFloorplanTour) {
        logger.log('📥 Floorplan/Tour cache is stale - will fetch fresh data');
        jobs.push({
          north_id: id,
          user_id: user.id,
          team_id: teamId,
          job_type: 'fetch_floorplan_tour',
          priority: 8,
          config: { zpid: scrapedData.zpid }
        });
      } else {
        logger.log('✓ Floorplan/Tour cache is fresh - skipping fetch');
      }

      if (needsNeighborhoodData) {
        logger.log('📥 Neighborhood cache is stale - will fetch fresh data');
        jobs.push({
          north_id: id,
          user_id: user.id,
          team_id: teamId,
          job_type: 'fetch_neighborhood_data',
          priority: 7,
          config: { zip: scrapedData.zip }
        });
      } else {
        logger.log('✓ Neighborhood cache is fresh - skipping fetch');
      }

      // Always regenerate neighborhood summary (AI summaries should always be fresh)
      jobs.push({
        north_id: id,
        user_id: user.id,
        team_id: teamId,
        job_type: 'generate_neighborhood_summary',
        priority: 6,
        config: {}
      });

      // Always regenerate executive summary (AI summaries should always be fresh)
      // Double-check no duplicate job exists before creating
      const { data: existingExecutiveJob } = await supabase
        .from('north_processing_jobs' as any)
        .select('id')
        .eq('north_id', id)
        .eq('job_type', 'generate_executive_summary')
        .in('status', ['pending', 'processing'])
        .maybeSingle();
      
      if (!existingExecutiveJob) {
        jobs.push({
          north_id: id,
          user_id: user.id,
          team_id: teamId,
          job_type: 'generate_executive_summary',
          priority: 5,
          config: { summaryType: 'executive' }
        });
      }

      // Always regenerate showings summary (AI summaries should always be fresh)
      jobs.push({
        north_id: id,
        user_id: user.id,
        team_id: teamId,
        job_type: 'generate_showings_summary',
        priority: 4,
        config: {}
      });

      if (jobs.length > 0) {
        // Insert jobs one at a time to ensure unique constraint is enforced
        // This prevents duplicate jobs from being inserted even in a single transaction
        const insertedJobs: any[] = [];
        const failedJobs: any[] = [];
        
        for (const job of jobs) {
          const { data: insertedJob, error: jobError } = await supabase
            .from('north_processing_jobs' as any)
            .insert(job)
            .select()
            .single();
          
          if (jobError) {
            // If it's a unique constraint violation, that's OK - job already exists
            if (jobError.code === '23505' || jobError.message?.includes('unique') || jobError.message?.includes('duplicate')) {
              // Try to find the existing job
              const { data: existingJob, error: existingJobError } = await supabase
                .from('north_processing_jobs' as any)
                .select('id, status')
                .eq('north_id', job.north_id)
                .eq('job_type', job.job_type)
                .in('status', ['pending', 'processing'])
                .maybeSingle();
              
              if (!existingJobError && existingJob && 'id' in existingJob) {
                insertedJobs.push({ ...job, id: (existingJob as any).id });
              } else {
                failedJobs.push({ job, error: jobError });
              }
            } else {
              logger.error(`Error inserting ${job.job_type} job:`, jobError);
              failedJobs.push({ job, error: jobError });
            }
          } else if (insertedJob) {
            insertedJobs.push(insertedJob);
          }
        }
        
        if (failedJobs.length > 0 && failedJobs.length === jobs.length) {
          // All jobs failed - this is a real error
          logger.error('Error creating refresh jobs:', failedJobs);
          throw new Error(`Failed to create ${failedJobs.length} jobs`);
        } else if (failedJobs.length > 0) {
          // Some jobs failed, but some succeeded - log warning
          logger.warn(`${failedJobs.length} jobs failed to insert, but ${insertedJobs.length} succeeded`);
        }

        logger.log(`Created ${jobs.length} refresh jobs for north ${id} (saved API calls by using cache)`);
      } else {
        logger.log('All data is cached - only regenerating AI summaries');
      }

      // Progress dialog is already showing - jobs will be polled automatically
      // Executive summary is handled by the job queue (generate_executive_summary at priority 5)

    } catch (error) {
      logger.error("Error refreshing North:", error);
      toast({
        title: "Error",
        description: "Failed to refresh North data",
        variant: "destructive",
      });
      setRefreshingNorthId(null);
      setIsRefreshing(false);
      isRefreshingRef.current = false; // Update ref immediately
    }
  };

  const handlePublishOrEdit = async () => {
    if (!north || !id) return;

    try {
      if (north.is_published) {
        // Unpublish to allow editing (versioning approach)
        const { error: updateError } = await supabase
          .from("norths")
          .update({
            is_published: false,
            published_at: null,
          })
          .eq("id", id);

        if (updateError) throw updateError;

        toast({
          title: "Unpublished",
          description: "Report unpublished. Make your edits and publish when ready.",
        });

        // Refresh data to reflect unpublished state
        await fetchNorthData();
        // Refresh usage count to update available reports
        await refreshUsage();
      } else {
        // Check limit before publishing
        if (!checkLimit('norths')) {
          // Show over limit dialog instead of automatically unpublishing
          setShowOverLimitDialog(true);
          return;
        }

        // Publish the report
        // If no custom logo is set, use the company logo from profile
        let updateData: any = {
          is_published: true,
          is_draft: false,
          published_at: new Date().toISOString(),
        };

        if (!north.custom_logo_url && profile?.company_logo_url) {
          updateData.custom_logo_url = profile.company_logo_url;
        }

        const { error: updateError } = await supabase
          .from("norths")
          .update(updateData)
          .eq("id", id);

        if (updateError) throw updateError;

        await fetchNorthData();
        // Refresh usage count to reflect the new published report
        await refreshUsage();

        toast({
          title: "Published!",
          description: `View at: ${window.location.origin}/north/${id}`,
        });
      }
    } catch (error) {
      logger.error("Error publishing/editing:", error);
      toast({
        title: "Error",
        description: "Failed to publish or create draft",
        variant: "destructive",
      });
    }
  };

  const handleShare = async () => {
    if (!north || !id) return;

    // If not published, try to publish first
    if (!north.is_published) {
      // Check limit before publishing
      if (!checkLimit('norths')) {
        // Show over limit dialog instead of automatically unpublishing
        setShowOverLimitDialog(true);
        return;
      }

      try {
        // Publish the report
        let updateData: any = {
          is_published: true,
          is_draft: false,
          published_at: new Date().toISOString(),
        };

        if (!north.custom_logo_url && profile?.company_logo_url) {
          updateData.custom_logo_url = profile.company_logo_url;
        }

        const { error: updateError } = await supabase
          .from("norths")
          .update(updateData)
          .eq("id", id);

        if (updateError) throw updateError;

        await fetchNorthData();

        toast({
          title: "Published!",
          description: "Opening share dialog...",
        });
      } catch (error) {
        logger.error("Error publishing:", error);
        toast({
          title: "Error",
          description: "Failed to publish report",
          variant: "destructive",
        });
        return;
      }
    }

    // Open share dialog
    setShareDialogOpen(true);
  };

  // Compute derived values - moved after early return to ensure north/profile exist
  const fullAddress = north ? `${north.address}, ${north.city}, ${north.state} ${north.zip}` : '';
  const agentName = north ? (north.custom_agent_name || north.listing_agent || teamName || "") : "";
  // Use custom logo, then company logo, then default from platform settings
  const logoUrl = north ? (north.custom_logo_url || profile?.company_logo_url || defaultLogoUrl) : defaultLogoUrl;
  // Agent headshot should come from Design Settings first, then Zillow listing agent photo
  // Do NOT use profile avatar - that's for the user's profile, not the listing agent
  const agentHeadshot = north ? (north.custom_agent_headshot_url || north.listing_agent_photo) : null;
  
  // Use ONLY the user's account branding colors from their profile settings
  // Dashboard and NORTH reports are backend tools, so they should use account branding
  const primaryColor = profile?.default_primary_color || '#0c0f24';
  const secondaryColor = profile?.default_secondary_color || '#1b489b';

  const sidebarItems = useMemo(() => ([
    { id: 'preview', label: 'Preview your North', icon: Eye, tourTitle: 'Preview', tourCopy: 'See the live report as sellers will view it.' },
    { id: 'views-saves', label: 'Views & Saves', icon: BarChart, tourTitle: 'Views & Saves', tourCopy: 'Enter total views and saves for Zillow and Realtor.com.' },
    { id: 'social', label: 'Social Media', icon: MessageSquare, tourTitle: 'Social campaigns', tourCopy: 'Generate and review social content for the listing.' },
    { id: 'showings', label: 'Showings & Open Houses', icon: Building2, tourTitle: 'Showings', tourCopy: 'Track showings and open house activity.' },
    { id: 'tasks', label: 'Tasks', icon: ListChecks, tourTitle: 'Tasks', tourCopy: 'Log listing activities and milestones for sellers.' },
    { id: 'floorplan-tour', label: 'Floorplan & Tour', icon: LayoutDashboard, tourTitle: 'Floorplan & tours', tourCopy: 'Manage floorplans and virtual tour embeds.' },
    { id: 'comps', label: 'Comps', icon: ClipboardCheck, tourTitle: 'Comps', tourCopy: 'Review comparable listings and pricing insights.' },
    { id: 'neighborhood', label: 'Neighborhood Data', icon: MapPin, tourTitle: 'Neighborhood data', tourCopy: 'Show local insights, amenities, and market context.' },
    { id: 'agent-insights', label: 'Agent Insights', icon: Lightbulb, tourTitle: 'Agent insights', tourCopy: 'Capture agent feedback and key notes.' },
    { id: 'ai', label: 'AI Summaries', icon: Sparkles, tourTitle: 'AI summaries', tourCopy: 'Generate AI summaries for sellers and buyers.' },
    { id: 'documents', label: 'Documents', icon: Paperclip, tourTitle: 'Documents', tourCopy: 'Attach inspection reports, HOA docs, disclosures, and links for sellers.' },
  ]), []);

  const reportTourSteps = useMemo<TourStep[]>(() => [
    ...sidebarItems.map(item => ({
      id: `nav-${item.id}`,
      title: item.tourTitle,
      description: item.tourCopy,
      selector: `[data-tour="nav-${item.id}"]`,
      placement: 'right' as const,
    })),
    {
      id: 'nav-design',
      title: 'Design settings',
      description: 'Adjust branding, colors, and logos for this North.',
      selector: '[data-tour="nav-design"]',
      placement: 'bottom',
    },
    {
      id: 'actions',
      title: 'Refresh, publish, and share',
      description: 'Refresh data, publish or unpublish, and open the share dialog.',
      selector: '[data-tour="report-actions"]',
      placement: 'left',
    },
    {
      id: 'preview',
      title: 'Live preview',
      description: 'See how updates look before sharing with your sellers.',
      selector: '[data-tour="report-preview"]',
      placement: 'top',
    },
    {
      id: 'view-toggle',
      title: 'Device views',
      description: 'Switch between desktop and mobile to confirm the experience.',
      selector: '[data-tour="report-view-toggle"]',
      placement: 'bottom',
    },
  ], [sidebarItems]);

  const startReportTour = useCallback(() => {
    if (!reportTourSteps.length || tourActive || isStartingReportTour) return;
    setIsStartingReportTour(true);
    startTour(reportTourSteps, {
      maskPadding: 10,
      onFinish: () => {
        markTourSeen("has_seen_report_tour").finally(() => setIsStartingReportTour(false));
        setHasSeenReportTour(true);
      },
      onExit: () => {
        markTourSeen("has_seen_report_tour").finally(() => setIsStartingReportTour(false));
        setHasSeenReportTour(true);
      }
    });
  }, [isStartingReportTour, reportTourSteps, startTour, tourActive]);

  useEffect(() => {
    if (!isLoading && north && hasSeenReportTour === false && !tourActive && !isStartingReportTour) {
      const timer = setTimeout(startReportTour, 500);
      return () => clearTimeout(timer);
    }
  }, [hasSeenReportTour, isLoading, north, tourActive, isStartingReportTour, startReportTour]);

  let fallbackContent: JSX.Element | null = null;
  if (isLoading) {
    fallbackContent = (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">Loading...</p>
          <p className="text-xs text-muted-foreground">Fetching North report data...</p>
        </div>
      </div>
    );
  } else if (!north) {
    fallbackContent = (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">North not found</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (fallbackContent) {
    return fallbackContent;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-20 bg-card shadow-sm" data-tour="report-nav">
        <div className="flex items-center justify-between px-3 md:px-6 py-3 border-b border-border">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Mobile Menu Button */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden" data-tour="nav-trigger">
                <Button
                  variant="ghost"
                  size="icon"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = hoverColor;
                    e.currentTarget.style.color = getContrastColor(hoverColor);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '';
                  }}
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 [&>button]:hidden">
                <div className="p-4 space-y-1">
                  {/* Back to Dashboard */}
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
                    onClick={() => { setIsMobileMenuOpen(false); navigate('/dashboard'); }}
                  >
                    <ArrowLeft className="h-5 w-5 flex-shrink-0" />
                    <span className="whitespace-nowrap">Back to Dashboard</span>
                  </Button>
                  <div className="border-t border-border my-2" />
                  {sidebarItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    return (
                    <Button
                      data-tour={`nav-${item.id}`}
                        key={item.id}
                        variant="ghost"
                        className={`w-full justify-start gap-3 ${!isActive ? 'text-muted-foreground hover:text-foreground' : ''}`}
                        style={isActive && profile && north ? {
                          backgroundColor: effectiveSecondaryColor,
                          color: getContrastColor(effectiveSecondaryColor),
                        } : undefined}
                        onMouseEnter={(e) => {
                          if (!isActive && hoverColor) {
                            e.currentTarget.style.backgroundColor = hoverColor;
                            e.currentTarget.style.color = getContrastColor(hoverColor);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '';
                          }
                        }}
                        onClick={() => {
                          updateActiveSection(item.id);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        <span className="whitespace-nowrap">{item.label}</span>
                      </Button>
                    );
                  })}
                  <div className="border-t border-border my-2" />
                  <Button
                    data-tour="nav-design"
                    variant="ghost"
                    className={`w-full justify-start gap-3 ${activeSection !== 'design' ? 'text-muted-foreground hover:text-foreground' : ''}`}
                    style={activeSection === 'design' && profile && north ? {
                      backgroundColor: effectiveSecondaryColor,
                      color: getContrastColor(effectiveSecondaryColor),
                    } : undefined}
                    onMouseEnter={(e) => {
                      if (activeSection !== 'design' && hoverColor) {
                        e.currentTarget.style.backgroundColor = hoverColor;
                        e.currentTarget.style.color = getContrastColor(hoverColor);
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== 'design') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '';
                      }
                    }}
                    onClick={() => {
                      updateActiveSection('design');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <Palette className="h-5 w-5 flex-shrink-0" />
                    <span className="whitespace-nowrap">Design Settings</span>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="hidden md:flex"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = hoverColor;
                e.currentTarget.style.color = getContrastColor(hoverColor);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '';
              }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>

            {north.image_url && (
              <img
                src={north.image_url}
                alt={north.address}
                className="h-8 w-8 md:h-10 md:w-10 rounded object-cover"
              />
            )}

            <div className="hidden sm:block">
              <h1 className="font-semibold text-foreground text-sm md:text-base">{fullAddress}</h1>
            </div>

            <Badge variant="secondary" className={`text-xs ${
              north.is_published && !north.is_draft
                ? 'bg-green-600 text-white'
                : north.is_draft
                ? 'bg-orange-600 text-white'
                : 'bg-gray-600 text-white'
            }`}>
              {north.is_published && !north.is_draft ? 'Published' : north.is_draft ? 'Draft' : 'Unpublished'}
            </Badge>
          </div>

          <div className="flex items-center gap-1 md:gap-2" data-tour="report-actions">
            <Button
              variant="outline"
              size="sm"
              onClick={startReportTour}
              className="hidden md:inline-flex"
            >
              View tutorial
            </Button>
            <Button
              data-tour="nav-design"
              variant="outline"
              size="sm"
              onClick={() => updateActiveSection('design')}
              className="hidden md:flex"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = hoverColor;
                e.currentTarget.style.color = getContrastColor(hoverColor);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '';
              }}
            >
              <Palette className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshNorth}
              disabled={isRefreshing}
              className="hidden md:flex"
              onMouseEnter={(e) => {
                if (!isRefreshing) {
                  e.currentTarget.style.backgroundColor = hoverColor;
                  e.currentTarget.style.color = getContrastColor(hoverColor);
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '';
              }}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh North'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePublishOrEdit}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = hoverColor;
                e.currentTarget.style.color = getContrastColor(hoverColor);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '';
              }}
            >
              <LayoutDashboard className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">{north.is_published && !north.is_draft ? 'Unpublish' : 'Publish'}</span>
            </Button>
            <Button
              size="sm"
              onClick={handleShare}
              style={profile && north ? {
                backgroundColor: effectiveSecondaryColor,
                color: getContrastColor(effectiveSecondaryColor),
              } : undefined}
            >
              <Share2 className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Share</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Horizontal Tab Bar - Desktop Only */}
      <div className="hidden lg:block sticky top-[65px] z-10 bg-card border-b border-border -mt-px">
        <div className="flex items-center gap-1 px-6 py-2.5 overflow-x-auto scrollbar-hide">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <Button
                        data-tour={`nav-${item.id}`}
                key={item.id}
                variant="ghost"
                size="sm"
                className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md ${!isActive ? 'text-muted-foreground hover:text-foreground' : ''}`}
                style={isActive && profile && north ? {
                  backgroundColor: effectiveSecondaryColor,
                  color: getContrastColor(effectiveSecondaryColor),
                } : undefined}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = hoverColor;
                    e.currentTarget.style.color = getContrastColor(hoverColor);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '';
                  }
                }}
                onClick={() => updateActiveSection(item.id)}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium">{item.label}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 bg-muted/30">
          {/* Content Area */}
          {activeSection === 'design' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4">
              <NorthDesignSettings
                northId={north.id}
                customAgentName={north.custom_agent_name}
                customAgentHeadshotUrl={north.custom_agent_headshot_url}
                customLogoUrl={north.custom_logo_url}
                primaryBrandColor={north.primary_brand_color}
                secondaryBrandColor={north.secondary_brand_color}
                defaultPrimaryColor={profile?.default_primary_color || '#0c0f24'}
                defaultSecondaryColor={profile?.default_secondary_color || '#1b489b'}
                defaultLogoUrl={profile?.company_logo_url}
                sellerNames={(north as any).seller_names}
                isPublished={north.is_published && !north.is_draft}
                useCustomBranding={(north as any).use_custom_branding || false}
                onUpdate={fetchNorthData}
              />
            </div>
          ) : activeSection === 'ai' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4">
              <NorthAISummaries
                key={`${north.id}-${north.executive_summary_updated_at}-${north.showings_summary_updated_at}-${north.comps_summary_updated_at}-${north.neighborhood_summary_updated_at}`}
                northId={north.id}
                northData={north}
                executiveSummary={north.executive_summary}
                executiveSummaryUpdatedAt={north.executive_summary_updated_at}
                showingsSummary={north.showings_summary}
                showingsSummaryUpdatedAt={north.showings_summary_updated_at}
                compsSummary={north.comps_summary}
                compsSummaryUpdatedAt={north.comps_summary_updated_at}
                neighborhoodSummary={north.neighborhood_summary}
                neighborhoodSummaryUpdatedAt={north.neighborhood_summary_updated_at}
                neighborhoodDataUpdatedAt={north.neighborhood_data_updated_at}
                isPublished={north.is_published && !north.is_draft}
                secondaryColor={effectiveSecondaryColor}
                onUpdate={fetchNorthData}
              />
            </div>
          ) : activeSection === 'social' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4 p-6">
              <SocialMediaManager
                northId={north.parent_north_id || north.id}
                isPublished={north.is_published && !north.is_draft}
                primaryColor={effectivePrimaryColor}
                secondaryColor={effectiveSecondaryColor}
                onUpdate={fetchNorthData}
              />
            </div>
          ) : activeSection === 'showings' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4">
              <ShowingsManager
                northId={north.parent_north_id || north.id}
                isPublished={north.is_published && !north.is_draft}
                onUpdate={fetchNorthData}
                primaryColor={effectivePrimaryColor}
                secondaryColor={effectiveSecondaryColor}
                hoverColor={hoverColor}
              />
            </div>
          ) : activeSection === 'tasks' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4">
              <TasksManager
                northId={north.parent_north_id || north.id}
                isPublished={north.is_published && !north.is_draft}
                onUpdate={fetchNorthData}
                primaryColor={effectivePrimaryColor}
                secondaryColor={effectiveSecondaryColor}
                hoverColor={hoverColor}
              />
            </div>
          ) : activeSection === 'floorplan-tour' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4 p-6">
              <FloorplanTourManager
                northId={north.parent_north_id || north.id}
                zpid={north.zpid || undefined}
                floorplanData={north.floorplan_data}
                tourData={north.tour_data}
                floorplanUpdatedAt={north.floorplan_updated_at || undefined}
                tourUpdatedAt={north.tour_updated_at || undefined}
                onRefresh={fetchNorthData}
                onUpdate={fetchNorthData}
                isPublished={north.is_published && !north.is_draft}
                primaryColor={effectivePrimaryColor}
                secondaryColor={effectiveSecondaryColor}
              />
            </div>
          ) : activeSection === 'comps' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4 p-6">
              <CompsManager
                northId={north.parent_north_id || north.id}
                isPublished={north.is_published && !north.is_draft}
                primaryColor={effectivePrimaryColor}
                secondaryColor={effectiveSecondaryColor}
                onUpdate={fetchNorthData}
              />
            </div>
          ) : activeSection === 'neighborhood' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4">
              <NeighborhoodData
                northId={north.parent_north_id || north.id}
                northData={north}
                isPublished={north.is_published && !north.is_draft}
                primaryColor={effectivePrimaryColor}
                secondaryColor={effectiveSecondaryColor}
                onUpdate={fetchNorthData}
              />
            </div>
          ) : activeSection === 'agent-insights' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4">
              <AgentInsightsManager
                northId={north.parent_north_id || north.id}
                isPublished={north.is_published && !north.is_draft}
                onUpdate={fetchNorthData}
                secondaryColor={effectiveSecondaryColor}
              />
            </div>
          ) : activeSection === 'views-saves' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4">
              <ViewsSavesManager
                northId={north.parent_north_id || north.id}
                isPublished={north.is_published && !north.is_draft}
                primaryColor={effectivePrimaryColor}
                secondaryColor={effectiveSecondaryColor}
                onUpdate={fetchNorthData}
              />
            </div>
          ) : activeSection === 'documents' ? (
            <div className="max-w-6xl mx-auto bg-card rounded-lg m-4 p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold">Documents & Links</h2>
                <p className="text-sm text-muted-foreground mt-1">Attach inspection reports, HOA docs, disclosures, or any links you want visible on the seller report.</p>
              </div>
              <NorthDocuments
                northId={north.parent_north_id || north.id}
                teamId={profile?.team_id || ''}
              />
            </div>
          ) : (
            <>
              {/* Preview Container */}
              <div className="px-4 pb-8 pt-4 md:h-[calc(100vh-168px)] md:overflow-y-auto">
                {/* View Mode Toggle - Hidden on Mobile */}
                <div className="hidden md:flex justify-center mb-4" data-tour="report-view-toggle">
                  <div className="inline-flex rounded-lg border border-border bg-background p-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewMode('desktop')}
                      className={`gap-2`}
                      style={viewMode === 'desktop' && profile && north ? {
                        backgroundColor: effectiveSecondaryColor,
                        color: getContrastColor(effectiveSecondaryColor),
                      } : undefined}
                    >
                      <Monitor className="h-4 w-4" />
                      Desktop
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewMode('mobile')}
                      className={`gap-2`}
                      style={viewMode === 'mobile' && profile && north ? {
                        backgroundColor: effectiveSecondaryColor,
                        color: getContrastColor(effectiveSecondaryColor),
                      } : undefined}
                    >
                      <Smartphone className="h-4 w-4" />
                      Mobile
                    </Button>
                  </div>
                </div>

                <div className={`flex items-start justify-center ${viewMode === 'mobile' ? '' : 'px-3'}`}>
                  <Card
                    className={`shadow-lg transition-all duration-300 ${viewMode === 'mobile' ? 'w-full max-w-lg' : 'w-full max-w-6xl'}`}
                    style={{
                      '--primary': hexToHsl(effectivePrimaryColor),
                      '--secondary': hexToHsl(effectiveSecondaryColor),
                    } as React.CSSProperties}
                    data-tour="report-preview"
                  >
                    <CardContent className="p-0">
                {/* Logo Header */}
                <div className={`flex items-center justify-between border-b border-border gap-4 ${viewMode === 'mobile' ? 'p-4' : 'p-6'}`}>
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className={`object-contain ${viewMode === 'mobile' ? 'h-8 max-w-[35%]' : 'h-8 md:h-12 max-w-[40%]'}`} />
                  ) : (
                    <div className={`bg-muted rounded flex items-center justify-center text-xs text-muted-foreground ${viewMode === 'mobile' ? 'h-12 w-32' : 'h-16 w-48'}`}>
                      Logo
                    </div>
                  )}

                  <div className={`flex items-center ${viewMode === 'mobile' ? 'gap-2' : 'gap-3'}`}>
                    <div className={viewMode === 'mobile' ? 'text-right' : 'text-left order-first'}>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Listed By</p>
                      <p className={`font-semibold text-foreground ${viewMode === 'mobile' ? 'text-sm' : 'text-base'}`}>{agentName}</p>
                    </div>
                    {agentHeadshot ? (
                      <img
                        src={agentHeadshot}
                        alt={agentName}
                        className={`rounded-full object-cover ${viewMode === 'mobile' ? 'h-10 w-10' : 'h-12 w-12'}`}
                      />
                    ) : (
                      <div className={`rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold ${viewMode === 'mobile' ? 'h-10 w-10 text-xs' : 'h-12 w-12 text-sm'}`}>
                        {agentName.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Property Image */}
                {north.image_url && (
                  <div className="relative">
                    <img
                      src={north.image_url}
                      alt={fullAddress}
                      className={`w-full object-cover ${viewMode === 'mobile' ? 'h-64' : 'h-96'}`}
                    />
                  </div>
                )}

                {/* Property Details */}
                <div className={viewMode === 'mobile' ? 'p-6' : 'p-8'}>
                  <div className={viewMode === 'mobile' ? 'mb-6' : 'mb-8'}>
                    <div className={`flex mb-3 gap-2 ${viewMode === 'mobile' ? 'flex-col' : 'flex-row items-start justify-between'}`}>
                      <h2 className={`font-bold text-foreground ${viewMode === 'mobile' ? 'text-lg' : 'text-2xl'}`}>
                        {fullAddress}
                      </h2>
                      {north.price && (
                        <p className={`font-bold text-foreground whitespace-nowrap ${viewMode === 'mobile' ? 'text-lg' : 'text-2xl'}`}>
                          ${north.price.toLocaleString()}
                        </p>
                      )}
                    </div>

                    {/* Property Stats in single line */}
                    {(north.beds || north.baths || north.sqft) && (
                      <div className={`flex items-center gap-3 text-muted-foreground ${viewMode === 'mobile' ? 'text-sm' : 'text-base'}`}>
                        {north.beds && (
                          <>
                            <span>{north.beds} beds</span>
                            {(north.baths || north.sqft) && <span>•</span>}
                          </>
                        )}
                        {north.baths && (
                          <>
                            <span>{north.baths} baths</span>
                            {north.sqft && <span>•</span>}
                          </>
                        )}
                        {north.sqft && (
                          <span>{north.sqft.toLocaleString()} sqft</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* The Big Picture */}
                  <div className={`rounded-xl bg-gradient-to-br from-muted/5 via-muted/10 to-background border border-border/50 shadow-sm ${viewMode === 'mobile' ? 'mb-8 p-6' : 'mb-12 p-8'}`}>
                    <h3 className={`font-semibold tracking-wider text-foreground mb-4 ${viewMode === 'mobile' ? 'text-xs' : 'text-sm'}`}>
                      THE BIG PICTURE
                    </h3>
                    <div
                      className={`text-foreground/90 leading-relaxed ${viewMode === 'mobile' ? 'text-sm' : 'text-base'}`}
                      dangerouslySetInnerHTML={{
                        __html: north.executive_summary
                          ? formatMarkdownToSafeHtml(north.executive_summary)
                          : `We've seen <span class="px-2 py-0.5 bg-secondary text-secondary-foreground font-medium rounded">${(north.views_count || 0).toLocaleString()} Zillow views</span> and <span class="px-2 py-0.5 bg-secondary text-secondary-foreground font-medium rounded">${north.saves_count || 0} saves</span>, along with positive in-person feedback highlighting the home's condition and appeal, which confirms strong buyer interest. ${north.price_history && north.price_history.length > 0 ? '<span>With nearby listings making <span class="px-2 py-0.5 bg-secondary text-secondary-foreground font-medium rounded">notable price reductions</span></span>' : ''} and social engagement tapering slightly, this is a great time to refresh marketing efforts and re-energize visibility to convert interest into action.`
                      }}
                    />
                  </div>

                  {/* Listing Performance */}
                  <div className={viewMode === 'mobile' ? 'mb-8' : 'mb-12'}>
                    <ListingPerformance
                   northId={north.parent_north_id || north.id}
                      zillowViews={north.views_count}
                      primaryColor={effectivePrimaryColor}
                      secondaryColor={effectiveSecondaryColor}
                      forceMobileLayout={viewMode === 'mobile'}
                    />
                  </div>

                  {/* Social Campaigns - Only show if toggle is on AND there's data */}
                  <SocialCampaignsPreviewWrapper
                    northId={north.parent_north_id || north.id}
                    address={north.address}
                    showOnReport={north.show_social_on_report !== false}
                    viewMode={viewMode}
                  />

                  {/* Showings - Only show if toggle is on AND there's data */}
                  <ShowingsPreviewWrapper
                    northId={north.parent_north_id || north.id}
                    showingsSummary={north.showings_summary}
                    showOnReport={north.show_showings_on_report !== false}
                    viewMode={viewMode}
                  />

                  {/* Tasks - Only show if toggle is on AND there's data */}
                  <TasksPreviewWrapper
                    northId={north.parent_north_id || north.id}
                    showOnReport={north.show_tasks_on_report !== false}
                    viewMode={viewMode}
                  />

                  {/* Floorplan & Tour */}
                  {north.show_floorplan_tour_on_report !== false && (
                    <FloorplanTourPreview
                      northId={north.parent_north_id || north.id}
                      floorplanData={north.floorplan_data}
                      tourData={north.tour_data}
                      forceMobileLayout={viewMode === 'mobile'}
                      primaryColor={effectivePrimaryColor}
                      secondaryColor={effectiveSecondaryColor}
                      propertyImage={north.image_url || undefined}
                    />
                  )}

                  {/* Comps Section */}
                  {(() => {
                    // Check parent's visibility if parent exists, otherwise check current north's
                    const showComps = parentNorth 
                      ? parentNorth.show_comps_on_report === true
                      : north.show_comps_on_report !== false;
                    return showComps && (
                      <div className={`rounded-xl bg-gradient-to-br from-accent/5 via-primary/10 to-background border border-border/50 shadow-sm ${viewMode === 'mobile' ? 'mb-8 p-6' : 'mb-12 p-8'}`}>
                        <CompsPreview
                          northId={north.parent_north_id || north.id}
                          compsSummary={north.comps_summary}
                          forceMobileLayout={viewMode === 'mobile'}
                        />
                      </div>
                    );
                  })()}

                  {/* Neighborhood Data Section */}
                  {north.show_neighborhood_on_report !== false && north.neighborhood_data && (
                    <div 
                      className={`rounded-xl border border-border/50 shadow-sm ${viewMode === 'mobile' ? 'mb-8 p-6' : 'mb-12 p-8'}`}
                      style={getMarketPulseGradient}
                    >
                      <NeighborhoodDataPreview
                        neighborhoodData={north.neighborhood_data}
                        neighborhoodSummary={north.neighborhood_summary}
                        neighborhoodDataUpdatedAt={north.neighborhood_data_updated_at}
                        forceMobileLayout={viewMode === 'mobile'}
                      />
                    </div>
                  )}

                  {/* Agent Insights Section */}
                  <AgentInsightsSection
                    northId={north.parent_north_id || north.id}
                    primaryColor={effectivePrimaryColor}
                    forceMobileLayout={viewMode === 'mobile'}
                  />

                  {/* Documents & Links */}
                  <NorthDocuments
                    northId={north.parent_north_id || north.id}
                    teamId={profile?.team_id || ''}
                    readOnly
                  />

                  {/* Footer */}
                  <div className={`flex items-center border-t border-border ${viewMode === 'mobile' ? 'flex-col gap-3 pt-6 mt-6' : 'flex-row justify-between pt-8 mt-8'}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">Powered with</span>
                      <img src={defaultLogoUrl} alt="North Reports Logo" className={viewMode === 'mobile' ? 'h-6' : 'h-8'} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Last Updated: {new Date(north.last_refreshed).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    )}
        </div>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        northData={north}
        northId={north.id}
      />

      {/* Upgrade Dialog */}
      <UpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
        feature={upgradeFeature}
        currentPlan={subscription?.plan_tier}
        onContinueWithCurrentPlan={() => {
          // Close upgrade dialog and show over limit dialog
          setUpgradeDialogOpen(false);
          setShowOverLimitDialog(true);
        }}
      />

      {/* Over Limit Dialog */}
      {planLimits && usage && planLimits.max_active_norths !== null && (
        <OverLimitDialog
          open={showOverLimitDialog}
          onOpenChange={setShowOverLimitDialog}
          currentCount={usage.norths_created}
          maxAllowed={planLimits.max_active_norths}
          requireAtLeastOne={true}
          onUnpublishComplete={async () => {
            // Refresh usage first
            await refreshUsage();

            // Now try to publish this North
            if (north && id) {
              try {
                let updateData: any = {
                  is_published: true,
                  is_draft: false,
                  published_at: new Date().toISOString(),
                };

                if (!north.custom_logo_url && profile?.company_logo_url) {
                  updateData.custom_logo_url = profile.company_logo_url;
                }

                const { error: updateError } = await supabase
                  .from("norths")
                  .update(updateData)
                  .eq("id", id);

                if (updateError) throw updateError;

                await fetchNorthData();
                // Refresh usage count again to reflect the new published report
                await refreshUsage();

                toast({
                  title: "Published!",
                  description: `Your North Report is now live at: ${window.location.origin}/north/${id}`,
                });
              } catch (error) {
                logger.error("Error publishing after unpublish:", error);
                toast({
                  title: "Error",
                  description: "Reports were unpublished, but failed to publish this report. Please try again.",
                  variant: "destructive",
                });
              }
            }

            // Close the dialog
            setShowOverLimitDialog(false);
          }}
        />
      )}

      {/* Refresh Progress Dialog */}
      {refreshingNorthId && (
        <NorthLoadingProgress
          northId={refreshingNorthId}
          initialTimestamps={initialTimestamps || undefined}
          onComplete={async () => {
            // Guard: prevent duplicate calls
            if (!refreshingNorthId) {
              return;
            }
            
            // CRITICAL: Set cooldown timestamp IMMEDIATELY to prevent real-time subscription from interfering
            refreshCompleteTimeRef.current = Date.now();
            
            // Ensure minimum display time of 2 seconds for user feedback
            const elapsed = Date.now() - (window as any).refreshStartTime;
            const minDisplayTime = 2000;
            if (elapsed < minDisplayTime) {
              await new Promise(resolve => setTimeout(resolve, minDisplayTime - elapsed));
            }
            
            // Close dialog first (set state to prevent duplicate calls)
            const currentNorthId = refreshingNorthId;
            setRefreshingNorthId(null);
            setInitialTimestamps(null);
            
            // Small delay to ensure dialog closes before refreshing
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Wait for all database writes to complete (summaries might still be saving)
            await new Promise(resolve => setTimeout(resolve, 2000)); // Increased to 2 seconds
            
            // Single fetch with timestamp-based deduplication (fetchNorthData now handles this)
            await fetchNorthData();
            
            // Invalidate all queries
            queryClient.invalidateQueries({ queryKey: ['comps-preview', id] });
            queryClient.invalidateQueries({ queryKey: ['floorplan-tour', id] });
            queryClient.invalidateQueries({ queryKey: ['neighborhood-data', id] });
            queryClient.invalidateQueries({ queryKey: ['ai-summaries', id] });
            queryClient.invalidateQueries({ queryKey: ['north-showings-summary', id] });
            queryClient.invalidateQueries({ queryKey: ['agent-insights-summary', id] });
            queryClient.invalidateQueries({ queryKey: ['north-comprehensive', id] });
            queryClient.invalidateQueries({ queryKey: ['showings-preview', id] });
            queryClient.invalidateQueries({ queryKey: ['comps', id] });
            
            // Small delay before clearing isRefreshing to ensure all state updates complete
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Now clear isRefreshing flag - this allows real-time subscription to work again
            // But the cooldown will prevent it from triggering for 8 seconds
            setIsRefreshing(false);
            isRefreshingRef.current = false; // Update ref immediately
            
            toast({
              title: "Refresh Complete",
              description: "Your North Report has been updated!",
            });
          }}
          onDismiss={() => {
            setIsRefreshing(false);
            isRefreshingRef.current = false; // Update ref immediately
            setRefreshingNorthId(null);
            setInitialTimestamps(null);
            toast({
              title: "Updating in background",
              description: "Your report will continue to update as data is gathered.",
            });
          }}
        />
      )}
    </div>
  );
}

export default SellerReport;